import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CalendarToday,
  EmojiEvents,
  ExpandMore,
  ExpandLess,
  Person
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ScheduleMatch } from '@/services/scheduleService';

dayjs.extend(utc);

interface TBDMatchesSectionProps {
  matches: ScheduleMatch[];
}

const TBDMatchesSection: React.FC<TBDMatchesSectionProps> = ({ matches }) => {
  const [expanded, setExpanded] = useState(false);

  if (matches.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between"
        sx={{ cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
        onClick={handleToggle}
      >
        <Box display="flex" alignItems="center">
          <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6">
            Pending
          </Typography>
          <Chip 
            label={`${matches.length} match${matches.length !== 1 ? 'es' : ''}`} 
            size="small" 
            sx={{ ml: 2 }}
            variant="outlined"
          />
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
          Unscheduled or postponed matches
        </Typography>
        <List disablePadding>
          {matches.map((match, index) => {
            const isPostponed = match.status === 'POSTPONED';
            const hasDateTime = !!match.dateTime;

            // Format the date/time for display
            let formattedDateTime = '';
            if (hasDateTime && match.dateTime) {
              const dt = dayjs(match.dateTime);
              // Check if time is midnight (TBD time but has date)
              const isMidnightUTC = dt.utc().hour() === 0 && dt.utc().minute() === 0;
              if (isMidnightUTC) {
                formattedDateTime = dt.utc().format('MMM D, YYYY');
              } else {
                formattedDateTime = dt.format('MMM D, YYYY [at] h:mm A');
              }
            }

            return (
              <React.Fragment key={match.id}>
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        {match.homeTeam} vs {match.awayTeam}
                      </Typography>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <span style={{ display: 'block', marginTop: '4px' }}>
                        {/* Postponed chip */}
                        {isPostponed && (
                          <span style={{ display: 'block', marginBottom: '4px' }}>
                            <Chip
                              label="Postponed"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </span>
                        )}
                        {/* Player names */}
                        {match.players && match.players.length > 0 && (
                          <span style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                            <Person sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" component="span">
                              {match.players.map(p => p.name).join(', ')}
                            </Typography>
                          </span>
                        )}
                        {/* Date/time and location on one line */}
                        {(hasDateTime || match.location) && (
                          <span style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                            <CalendarToday sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                            <Typography
                              variant="body2"
                              color={isPostponed ? 'text.disabled' : 'text.secondary'}
                              component="span"
                              sx={isPostponed ? { textDecoration: 'line-through' } : undefined}
                            >
                              {hasDateTime && formattedDateTime}
                              {hasDateTime && match.location && ' @ '}
                              {match.location && match.location}
                            </Typography>
                          </span>
                        )}
                        {match.league && (
                          <span style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                            <EmojiEvents sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" component="span">
                              {match.league.division ? `${match.league.name} - ${match.league.division}` : match.league.name}
                            </Typography>
                          </span>
                        )}
                        {match.matchType && match.matchType !== 'REGULAR_SEASON' && (
                          <Typography variant="body2" color="text.secondary" component="span">
                            {match.matchType.replace('_', ' ')}
                          </Typography>
                        )}
                      </span>
                    }
                  />
                </ListItem>
                {index < matches.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      </Collapse>
    </Paper>
  );
};

export default TBDMatchesSection;
